import { parseISO, differenceInSeconds } from 'date-fns'

import type { SelectShippingModalProps } from '@b-stock/fe-core/bstock-next/components'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { isAuctionListing } from '@b-stock/fe-core/bstock-next/queries'
import type { LocationModel } from '@b-stock/location-api-client'
import type { ShipmentType } from '@b-stock/shipment-api-client'

import { buildGeofenceShippingSelectionProperties } from '@/buyer-portal/helpers/shipping'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

export const useShippingModalTracking = (
  listing:
    | OrchestratedAuctionListingDetails
    | OrchestratedOfferingListingDetails,
  selectedQuote?: OrchestratedQuote | null,
  {
    fromWithinRadius = false,
    getToWithinRadius = () => false,
  }: {
    fromWithinRadius?: boolean
    getToWithinRadius?: (id?: string) => boolean
  } = {}
) => {
  const { trackButtonClicked } = useAnalytics()
  const timeDiffInSeconds = ({ actualEndTime }: { actualEndTime?: string }) => {
    return differenceInSeconds(
      parseISO(actualEndTime || new Date().toDateString()),
      new Date()
    )
  }

  const auction = isAuctionListing(listing) ? listing.auction : undefined

  return {
    onConfirm: (data: {
      shipmentType: ShipmentType
      shippingCost?: number
      shipTo?: string
      address?: LocationModel
      isEligible?: boolean
    }) => {
      trackButtonClicked({
        buttonName: 'confirm_shipping_selection',
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        shipping_method: data.shipmentType,
        shipping_cost: data.shippingCost,
        address_id: data.address?.id,
        ship_to: data.shipTo,
        ...buildGeofenceShippingSelectionProperties({
          selectedQuote,
          toShipmentType: data.shipmentType,
          toAddressId: data.address?.id,
          fromWithinRadius,
          toWithinRadius: getToWithinRadius(data.address?.id),
        }),
      })
    },
    onCancel: (exitedWithoutChange: boolean) => {
      if (exitedWithoutChange) {
        trackButtonClicked({
          buttonName: 'cancel_address_modal',
          ...(auction?.actualEndTime && {
            time_left: timeDiffInSeconds({
              actualEndTime: auction.actualEndTime,
            }),
          }),
          bids_existing: auction?.numberOfBids ?? 0,
          modal_step: selectedQuote
            ? 'shipping_method_selection'
            : 'address_selection',
        })
      }
    },

    onLocationsViewed: ({
      eligibleCount,
      ineligibleCount,
    }: {
      eligibleCount: number
      ineligibleCount: number
    }) => {
      const action =
        eligibleCount > 0 && ineligibleCount > 0
          ? 'view_mixed_eligibility_addresses'
          : eligibleCount > 0
            ? 'view_eligible_addresses'
            : 'view_ineligible_addresses'
      trackButtonClicked({
        buttonName: action,
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        eligible_address_count: eligibleCount,
        ineligible_address_count: ineligibleCount,
      })
    },

    onAddressClicked: (
      address: LocationModel,
      isEligible: boolean,
      ineligibilityReasons?: string[]
    ) => {
      if (isEligible) {
        trackButtonClicked({
          buttonName: 'select_eligible_address',
          ...(auction?.actualEndTime && {
            time_left: timeDiffInSeconds({
              actualEndTime: auction.actualEndTime,
            }),
          }),
          bids_existing: auction?.numberOfBids ?? 0,
          is_default_address: address.attributes?.includes(
            'DEFAULT_DELIVERABLE'
          ),
        })
      } else {
        trackButtonClicked({
          buttonName: 'select_ineligible_address',
          ...(auction?.actualEndTime && {
            time_left: timeDiffInSeconds({
              actualEndTime: auction.actualEndTime,
            }),
          }),
          bids_existing: auction?.numberOfBids ?? 0,
          ineligibility_reasons: ineligibilityReasons ?? [],
        })
      }
    },

    onShippingMethodSelected: ({
      shipmentType,
      shipTo,
    }: Parameters<
      NonNullable<SelectShippingModalProps['onShippingMethodSelected']>
    >[0]) => {
      trackButtonClicked({
        buttonName: 'view_shipping_method',
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        shipping_method: shipmentType,
        ship_to: shipTo,
      })
    },

    onNewAddressLinkClicked: ({
      userHasExistingEligibleAddresses,
      userHasExistingIneligibleAddresses,
    }: {
      userHasExistingEligibleAddresses: boolean
      userHasExistingIneligibleAddresses: boolean
    }) => {
      trackButtonClicked({
        buttonName: 'add_address_link_from_modal',
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        modal_context: 'new_address',
        has_existing_eligible_addresses: userHasExistingEligibleAddresses,
        has_existing_ineligible_addresses: userHasExistingIneligibleAddresses,
      })
    },

    onNewAddressBlockClicked: ({
      userHasExistingEligibleAddresses,
      userHasExistingIneligibleAddresses,
    }: {
      userHasExistingEligibleAddresses: boolean
      userHasExistingIneligibleAddresses: boolean
    }) => {
      trackButtonClicked({
        buttonName: 'add_address_block_from_modal',
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        modal_context: 'new_address',
        has_existing_eligible_addresses: userHasExistingEligibleAddresses,
        has_existing_ineligible_addresses: userHasExistingIneligibleAddresses,
      })
    },

    onNewTaxExemptionClicked: ({
      userHasExistingEligibleAddresses,
      userHasExistingIneligibleAddresses,
    }: {
      userHasExistingEligibleAddresses: boolean
      userHasExistingIneligibleAddresses: boolean
    }) => {
      trackButtonClicked({
        buttonName: 'add_exemption_from_modal',
        ...(auction?.actualEndTime && {
          time_left: timeDiffInSeconds({
            actualEndTime: auction.actualEndTime,
          }),
        }),
        bids_existing: auction?.numberOfBids ?? 0,
        modal_context: 'add_new_tax_exemption',
        has_existing_eligible_addresses: userHasExistingEligibleAddresses,
        has_existing_ineligible_addresses: userHasExistingIneligibleAddresses,
      })
    },
  }
}
